import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { en } from '../i18n/locales/en';
import { zh } from '../i18n/locales/zh';

const STORAGE_KEY = 'site_language';
const dictionaries = { en, zh };

const LanguageContext = createContext(null);

function getByPath(obj, path) {
  return path.split('.').reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

function interpolate(value, vars) {
  if (typeof value !== 'string' || !vars) return value;
  return value.replace(/\{\{(\w+)\}\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : `{{${key}}}`
  );
}

function readStoredLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'zh') return stored;
  } catch {
    /* ignore */
  }
  return 'en';
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readStoredLanguage);

  useEffect(() => {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      /* ignore */
    }
  }, [language]);

  const setLanguage = useCallback((next) => {
    if (next === 'en' || next === 'zh') {
      setLanguageState(next);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState((prev) => (prev === 'en' ? 'zh' : 'en'));
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = dictionaries[language] || en;
      const value = getByPath(dict, key);
      if (value === undefined) {
        const fallback = getByPath(en, key);
        return interpolate(fallback ?? key, vars);
      }
      return interpolate(value, vars);
    },
    [language]
  );

  const tm = useCallback(
    (key) => {
      const dict = dictionaries[language] || en;
      const value = getByPath(dict, key);
      if (Array.isArray(value)) return value;
      const fallback = getByPath(en, key);
      return Array.isArray(fallback) ? fallback : [];
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      toggleLanguage,
      t,
      tm,
      isZh: language === 'zh',
    }),
    [language, setLanguage, toggleLanguage, t, tm]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
